import AdminPageHeader from "@/components/admin/page-header";
import { useLoadFeedback } from "@/hooks/use-load-feedback";
import { PageState } from "@/components/ui/page-state";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const empty = { name: "", description: "", sort_order: 0 };

export default function AdminCategories() {
  const { loading, refreshing, hasLoaded, error: loadError, run } = useLoadFeedback();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = React.useCallback(() => run(async () => setItems((await api.get("/categories")).data)), [run]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name) return toast.error("Nama wajib diisi");
    const payload = { ...form, sort_order: Number(form.sort_order) || 0 };
    try {
      if (editing) await api.put(`/categories/${editing.id}`, payload);
      else await api.post("/categories", payload);
      toast.success("Tersimpan"); setOpen(false); load();
    } catch { toast.error("Gagal"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Hapus kategori ini?")) return;
    await api.delete(`/categories/${id}`);
    toast.success("Terhapus"); load();
  };

  if (loading || (loadError && !hasLoaded)) return <PageState kind={loading ? "loading" : "error"} title={loading ? "Memuat kategori" : "Kategori belum tersedia"} description={loadError} onRetry={loadError ? load : undefined} />;

  return (
    <div aria-busy={refreshing} className="admin-page space-y-6" data-testid="admin-categories-page">
      {loadError && <PageState kind="error" title="Data belum dapat diperbarui" description={loadError} onRetry={load} />}
      <AdminPageHeader title="Kategori Menu" description={<> Kelola kategori menu </>} actions={<Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="rounded-full" data-testid="add-category-btn">
          <Plus className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Tambah Kategori</span><span className="sm:hidden">Tambah</span>
        </Button>} />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && <p className="text-muted-foreground">Belum ada kategori.</p>}
        {items.map((cat) => (
          <div key={cat.id} className="surface-card p-4" data-testid={`category-card-${cat.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display font-bold text-lg">{cat.name}</h3>
                <p className="text-xs text-muted-foreground mt-1">{cat.description || "—"}</p>
                <p className="text-xs text-muted-foreground mt-2">Urutan: {cat.sort_order}</p>
              </div>
              <div className="space-x-1">
                <Button variant="ghost" size="sm" onClick={() => { setEditing(cat); setForm(cat); setOpen(true); }}
                        aria-label={`Edit kategori ${cat.name}`} data-testid={`edit-cat-${cat.id}-btn`}><Pencil className="w-4 h-4"/></Button>
                <Button variant="ghost" size="sm" aria-label={`Hapus kategori ${cat.name}`} onClick={() => remove(cat.id)} data-testid={`delete-cat-${cat.id}-btn`}>
                  <Trash2 className="w-4 h-4 text-destructive"/>
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="admin-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Tambah"} Kategori</DialogTitle><DialogDescription>Kelompok kategori untuk mengelompokkan menu (contoh: Makanan, Minuman).</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label htmlFor="admincategories-field-1">Nama</Label><Input id="admincategories-field-1" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="bg-white" data-testid="cat-name-input"/></div>
            <div><Label htmlFor="admincategories-field-2">Deskripsi</Label><Textarea id="admincategories-field-2" rows={2} value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} className="bg-white"/></div>
            <div><Label htmlFor="admincategories-field-3">Urutan</Label><Input id="admincategories-field-3" type="number" value={form.sort_order} onChange={(e)=>setForm({...form,sort_order:e.target.value})} className="bg-white"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} data-testid="save-cat-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
